import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { CircularProgress, Stack, useMediaQuery, Typography, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import pickMarker from "./assets/pick_marker.png";
import { GOOGLE_MAPS_API_KEY } from "../../config/env";

const GoogleMapComponent = ({
  setDisablePickButton,
  setLocationEnabled,
  setLocation,
  setCurrentLocation,
  locationLoading,
  location,
  setPlaceDetailsEnabled,
  placeDetailsEnabled,
  locationEnabled,
  setPlaceDescription,
  height,
  isModalExpand,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const containerStyle = {
    width: "100%",
    maxHeight: "50dvh",
    height: isModalExpand
      ? "70vh"
      : height
      ? height
      : isSmall
      ? "350px"
      : "350px",
    paddingBottom: "0px",
  };
  const center = useMemo(
    () => ({
      lat: parseFloat(location?.lat),
      lng: parseFloat(location?.lng),
    }),
    [location?.lat, location?.lng]
  );

  const options = useMemo(
    () => ({
      zoomControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    }),
    []
  );
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
  });

  // Log any loading errors
  useEffect(() => {
    if (loadError) {
      console.error('Google Maps failed to load:', loadError);
    }
    if (!GOOGLE_MAPS_API_KEY) {
      console.error('Google Maps API key is missing. Please configure REACT_APP_GOOGLE_MAP_KEY in your .env file');
    }
  }, [loadError]);
  const [mapSetup, setMapSetup] = useState(false);
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(10);
  const [centerPosition, setCenterPosition] = useState(center);

  const onLoad = useCallback(function callback(map) {
    setZoom(17);
    setMap(map);
  }, []);
  useEffect(() => {
    if (location && placeDetailsEnabled) {
      setCenterPosition(location);
    }
    if (map?.center && mapSetup) {
      setCenterPosition({
        lat: map.center?.lat(),
        lng: map.center?.lng(),
      });
    }
  }, [map, mapSetup, placeDetailsEnabled, location]);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
    // setMapSetup(false)
  }, []);

  // Show error if API key is missing or if there's a load error
  if (!GOOGLE_MAPS_API_KEY || loadError) {
    return (
      <Stack
        padding="0px"
        sx={{
          boxShadow: "inset 0px 4px 4px rgba(0, 0, 0, 0.1)",
          borderRadius: "10px",
          p: "4px",
          height: containerStyle.height,
          width: containerStyle.width,
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            textAlign: "center",
            backgroundColor: "background.paper",
            borderRadius: "10px",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            {!GOOGLE_MAPS_API_KEY 
              ? "Google Maps API Key Missing" 
              : "Unable to Load Google Maps"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {!GOOGLE_MAPS_API_KEY 
              ? "Please configure REACT_APP_GOOGLE_MAP_KEY in your .env file"
              : loadError?.message || "Please check your internet connection or try refreshing the page"}
          </Typography>
        </Box>
      </Stack>
    );
  }

  // Show loading state while map is loading
  if (!isLoaded) {
    return (
      <Stack
        padding="0px"
        sx={{
          boxShadow: "inset 0px 4px 4px rgba(0, 0, 0, 0.1)",
          borderRadius: "10px",
          p: "4px",
          height: containerStyle.height,
          width: containerStyle.width,
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "background.paper",
            borderRadius: "10px",
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading map...
          </Typography>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      padding="0px"
      sx={{
        boxShadow: "inset 0px 4px 4px rgba(0, 0, 0, 0.1)",
        borderRadius: "10px",
        p: "4px",
      }}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center ?? centerPosition}
        onLoad={onLoad}
        zoom={zoom}
        onUnmount={onUnmount}
        onMouseDown={(e) => {
          setMapSetup?.(true);
          setDisablePickButton?.(true);
        }}
        onMouseUp={(e) => {
          setMapSetup?.(false);
          setDisablePickButton?.(false);
          setLocationEnabled?.(true);
          setLocation?.({
            lat: map.center?.lat(),
            lng: map.center?.lng(),
          });
          setCenterPosition?.({
            lat: map.center?.lat(),
            lng: map.center?.lng(),
          });
          setPlaceDetailsEnabled?.(false);
          setPlaceDescription?.(undefined);
        }}
        //  yesIWantToUseGoogleMapApiInternals
        onZoomChanged={() => {
          // setMapSetup(true)
          if (map) {
            setLocationEnabled?.(true);
            setLocation?.({
              lat: map.center?.lat(),
              lng: map.center?.lng(),
            });
            setCenterPosition({
              lat: map.center?.lat(),
              lng: map.center?.lng(),
            });
            // setPlaceDetailsEnabled(false)
          }
        }}
        options={options}
      >
        {!locationLoading ? (
          <img
            src={pickMarker.src}
            alt="Location marker"
            style={{
              zIndex: 3,
              position: "absolute",
              marginTop: -63,
              marginLeft: -32,
              left: "50%",
              top: "50%",
              height: "60px",
              width: "45px",
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            style={{
              zIndex: 3,
              position: "absolute",
              marginTop: -37,
              marginLeft: -11,
              left: "50%",
              top: "50%",
            }}
          >
            <CircularProgress />
          </Stack>
        )}
      </GoogleMap>
    </Stack>
  );
};

export default GoogleMapComponent;
